import _ from 'lodash';

const RETRY_CONFIG = {
  maxRetries: 3,
  timeout: 30000, // 30 seconds
  delay: 1000 // 1 second delay between retries
};

/**
 * Retry logic for async functions
 * @param {Function} asyncFn - The async function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} timeout - Timeout for each attempt in milliseconds
 * @param {number} delay - Delay between retries in milliseconds
 */
export const retryAsync = async (asyncFn, maxRetries = RETRY_CONFIG.maxRetries, timeout = RETRY_CONFIG.timeout, delay = RETRY_CONFIG.delay) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), timeout);
      });
      
      // Race between the actual request and timeout
      const result = await Promise.race([asyncFn(), timeoutPromise]);
      return result;
    } catch (error) {
      lastError = error;
      
      // Don't retry on client errors (4xx) - these are usually permanent failures
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        throw error;
      }
      
      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Wait before retrying (exponential backoff)
      const retryDelay = delay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      
    }
  }
  
  throw lastError;
};

/**
 * Retry logic for callback-based functions
 * @param {Function} callbackFn - The callback-based function to retry
 * @param {Function} callback - The final callback to call
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} timeout - Timeout for each attempt in milliseconds
 * @param {number} delay - Delay between retries in milliseconds
 */
export const retryCallback = (callbackFn, callback, maxRetries = RETRY_CONFIG.maxRetries, timeout = RETRY_CONFIG.timeout, delay = RETRY_CONFIG.delay) => {
  let attempt = 0;
  
  const attemptRequest = () => {
    const timeoutId = setTimeout(() => {
      handleError(new Error('Request timeout'));
    }, timeout);
    
    const wrappedCallback = (error, data, response) => {
      clearTimeout(timeoutId);
      
      if (!error) {
        return callback(null, data, response);
      }
      
      handleError(error, data, response);
    };
    
    const handleError = (error, data, response) => {
      // Don't retry on client errors (4xx) - these are usually permanent failures
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        return callback(error, data, response);
      }
      
      if (attempt >= maxRetries) {
        return callback(error, data, response);
      }
      
      attempt++;
      const retryDelay = delay * Math.pow(2, attempt - 1);
      
      setTimeout(() => {
        attemptRequest();
      }, retryDelay);
    };
    
    try {
      callbackFn(wrappedCallback);
    } catch (error) {
      handleError(error);
    }
  };
  
  attemptRequest();
};

/**
 * Check if an error should be retried
 * @param {Error} error - The error to check
 * @returns {boolean} - Whether the error should be retried
 */
export const shouldRetry = (error) => {
  // Don't retry on client errors (4xx) - these are usually permanent failures
  if (error.response && error.response.status >= 400 && error.response.status < 500) {
    return false;
  }
  
  return true;
};

export default {
  retryAsync,
  retryCallback,
  shouldRetry,
  RETRY_CONFIG
}; 